import { Container, SkeletonText, Table, Image, VStack, Flex, Box, Button, Text, useDisclosure, Center, Spinner } from "@chakra-ui/react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import TitleBar from "../../components/UI/TitleBar"
import { userHasPermission } from "../../utils"
import { NoPermission } from "../../components/UI/no-permission"
import { lazy, Suspense, useEffect, useMemo, useState } from "react"
import Navbar from "../../components/Common/Navbar"
import { PublicationsTableProps, readPublications } from "../../api/publications"
import { z } from "zod"
import { PublicationsOrderBy } from "../../client"
import { useQuery } from "@tanstack/react-query"
import Sort from "../../components/Common/Sort"
import ActionsMenu from "../../components/Common/ActionsMenu"
import { PaginationFooter } from "../../components/Common/PaginationFooter"

const DetailPublication = lazy(() => import("../../components/Publications/DetailPublication"))
const AddPublication = lazy(() => import("../../components/Publications/AddPublication"))
const PER_PAGE = 25

const tagsSearchSchema = z.object({
  page: z.number().catch(1),
})

export const Route = createFileRoute("/_layout/publications")({
  component: Publications,
  validateSearch: (search) => tagsSearchSchema.parse(search),
})


function PublicationsTable({pu_create, pu_update, pu_delete}: PublicationsTableProps) {
  const fields = [
    { title: "ID", col: "classic_id", align: undefined },
    { title: "Bild", col: null, align: undefined },
    { title: "Titel", col: "title", align: undefined },
    { title: "Kategorien", col: null, align: undefined },
    { title: "Tags", col: null, align: undefined },
    { title: "Publikationsdatum", col: "publication_dt", align: undefined },
    { title: "Aktionen", col: null, align: "right" as const },
  ]

  const [searchValue, setSearchValue] = useState<string>("")

  const handleSearchValueChange = (value: string) => {
    setSearchValue(value)
  }

  const { page } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const setPage = (page: number) =>
    navigate({
      search: (prev: { [key: string]: string }) => ({ ...prev, page }),
    })

  // Set page on search value change
  const [prevPage, setPrevPage] = useState<number>(page)
  useEffect(() => {
    if(searchValue == "") {
      setPage(prevPage)
    } else {
      setPrevPage(page)
      setPage(1)
    }
  }, [searchValue])

  // Sorting
  const [columnName, setColumnName] = useState<PublicationsOrderBy>()
  const [orderBy, setOrderBy] = useState<string>("")
  const [isDesc, setIsDesc] = useState<boolean>()

  const [showHidden, setShowHidden] = useState<boolean>(false)

  // Query
  const queryOptions = useMemo(() =>
    readPublications({
      per_page: PER_PAGE,
      page: page,
      order_by: columnName,
      descending: isDesc,
      query: searchValue,
      showOnlyHidden: showHidden,
    }),
  [page, columnName, isDesc, searchValue, showHidden])

  const {
    data: publications,
    isPending,
    isPlaceholderData,
  } = useQuery({
    ...queryOptions,
    placeholderData: (prevData) => prevData,
  })

  // Sorting
  const sortTable = (name: PublicationsOrderBy) => {
    if (columnName !== name) {
      setColumnName(name)
      setOrderBy("")
      sorting("")
    } else {
      sorting(orderBy)
    }
  }

  const sorting = (order: string) => {
    switch (order) {
      case "":
        setOrderBy("desc")
        setIsDesc(true)
        break
      case "desc":
        setOrderBy("asc")
        setIsDesc(false)
        break
      case "asc":
        setOrderBy("")
        setIsDesc(undefined)
        setColumnName(undefined)
        break
    }
  }

  // Open media detail view
  const detailModal = useDisclosure()
  const [detailPublication, setDetailPublication] = useState("")

  const openModal = (publicationId: string) => {
    setDetailPublication(publicationId)
    detailModal.onOpen()
  }

  return (
    <>
      <Flex alignItems="center">
        <Box w="full">
           <Navbar
              type="Publikation"
              addAllowed={pu_create && !showHidden}
              addModalAs={AddPublication}
              showSearch={true}
              onSearchChange={handleSearchValueChange}
            />
        </Box>

        <Button 
          variant="primary" 
          ml={4} 
          onClick={() => setShowHidden(!showHidden)}
        >
          {showHidden ? "Veröffentlichte" : "Archiv"}
        </Button>
      </Flex>

      {detailModal.open &&
        <Suspense fallback={
          <Center
            bg="rgba(0, 0, 0, 0.3)"
            position="fixed"
            top="0"
            left="0"
            width="100vw"
            height="100vh"
            zIndex="10000"
          >
            <Spinner size="lg" color="white" />
          </Center>}
        >
          <DetailPublication
            isOpen={detailModal.open}
            onClose={detailModal.onClose}
            publicationId={detailPublication}
          />
        </Suspense>
      }

      <Table.ScrollArea asChild>
        <Table.Root size={{ base: "sm", md: "md" }} whiteSpace="normal" display={{md: "table"}}>
          <Table.Header>
            <Table.Row>
              {fields.map((field, i) =>
                field.col != null ? (
                  <Table.ColumnHeader
                    onClick={() => sortTable(field.col as PublicationsOrderBy)}
                    cursor="pointer"
                    textAlign={field.align}
                    key={i}
                  >
                    {field.title}
                    {columnName === field.col &&
                      <Sort
                        columnNamePassed={columnName}
                        orderByPassed={orderBy}
                      />
                    }
                  </Table.ColumnHeader>
                ) : (
                  <Table.ColumnHeader textAlign={field.align} key={field.title}>
                    {field.title}
                  </Table.ColumnHeader>
                ),
              )}
            </Table.Row>
          </Table.Header>
          {isPending ? (
            <Table.Body>
              <Table.Row>
                {new Array(6).fill(null).map((_, index) => (
                  <Table.Cell key={index}>
                    <SkeletonText noOfLines={1} paddingBlock="16px" />
                  </Table.Cell>
                ))}
              </Table.Row>
            </Table.Body>
          ) : (
            <Table.Body>
              {!publications?.data ? (
                <Table.Row>
                  <Table.Cell colSpan={7} textAlign="center">
                    Keine Publikationen verfügbar
                  </Table.Cell>
                </Table.Row>
              ) : (
                publications?.data.map((publication) => (
                  <Table.Row key={publication.id} opacity={isPlaceholderData ? 0.5 : 1}>
                    <Table.Cell onClick={() => openModal(publication.id)} cursor="pointer">
                      {publication.classic_id}
                    </Table.Cell>

                    <Table.Cell onClick={() => openModal(publication.id)} cursor="pointer">
                      {publication.lead_image &&
                        <Image src={publication.lead_image.path.replace(/-(LG)\./, "-MD.")} alt={publication.lead_image.file_name} h="8em" />
                      }
                    </Table.Cell>

                    <Table.Cell onClick={() => openModal(publication.id)} cursor="pointer">
                      {publication.title}
                    </Table.Cell>

                    <Table.Cell onClick={() => openModal(publication.id)} cursor="pointer">
                      <VStack alignItems="flex-start">
                        {publication.fk_categories.map((cateogry) => (
                          <Text key={cateogry.name}>{cateogry.name}</Text>
                        ))}
                      </VStack>
                    </Table.Cell>

                    <Table.Cell onClick={() => openModal(publication.id)} cursor="pointer">
                      <VStack alignItems="flex-start">
                        {publication.fk_tags.map((tag) => (
                          <Text key={tag.name}>{tag.name}</Text>
                        ))}
                      </VStack>
                    </Table.Cell>

                    <Table.Cell onClick={() => openModal(publication.id)} cursor="pointer">
                      {new Date(`${publication.publication_dt}`).toLocaleString("de-CH", {
                        timeZone: "Europe/Zurich",
                      })}
                    </Table.Cell>

                    <Table.Cell textAlign="right">
                      <ActionsMenu
                        type="Publikation"
                        value={publication}
                        disableDelete={!pu_delete}
                        disableEdit={!pu_update}
                        showArchive={pu_update}
                        is_archived={publication.is_hidden}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          )}
        </Table.Root>
      </Table.ScrollArea>
      <PaginationFooter
        onChangePage={setPage}
        page={page}
        count={publications?.count ? publications?.count : 0}
        perPage={PER_PAGE}
      />
    </>
  )
}


function Publications() {
  // Permission check
  const [pu_create, pu_read, pu_update, pu_delete] = useMemo(() => {
    return ["create", "read", "update", "delete"].map(
      (action) => userHasPermission([{ perm: `publications_${action}`, is_allowed: false }])[0].is_allowed
    )
  }, [userHasPermission])

  if(!pu_read) return <NoPermission/>

  return (
    <Container maxW="full">
      <TitleBar title="Publikationen" />

      <PublicationsTable
        pu_create={pu_create}
        pu_update={pu_update}
        pu_delete={pu_delete}
      />
    </Container>
  )
}
